import { useCallback, useMemo, useState } from 'react';
import { buildRepriceCandidate } from 'pricing/repriceCandidate';
import { applyOverrides } from 'pricing/applyOverrides';
import { buildPersistEvents, recordPersistEvents, actorFromUser } from 'utils/execPricingAudit';
import { fetchCatalogRows } from 'utils/catalogFetch';
import { VERSION } from 'version';
import type { RepriceState } from 'shared/state/quoteActionsContext';
import type { ExecCtxValue } from 'shared/state/configContexts';
import type { AppUser, BomRow, PriceEditionsApi, Vendor } from 'shared/state/types';

type ToastFn = (t: { badge?: string; title: string; message: string }) => void;

type Deps = {
  exec: ExecCtxValue;
  config: Record<string, any>;
  priceBook: unknown;
  bom: BomRow[] | null;
  user: AppUser;
  vendor: Vendor;
  /** The live PriceEdition, for its `asOf` — see startReprice below. */
  priceEditions: PriceEditionsApi;
  showToast: ToastFn;
};

/** Sums BOM quantity by part number. Exported and unit-tested on its own —
 *  the reprice dialog extends each unit delta by quantity, and a part on more
 *  than one BOM row must report its true quote quantity (summed, not
 *  last-wins), matching buildPersistEvents' own scoping. */
export function sumQtyByPartNumber(bom: BomRow[] | null | undefined): Record<string, number> {
  const q: Record<string, number> = {};
  for (const row of bom ?? []) {
    if (row.partNumber) q[row.partNumber] = (q[row.partNumber] ?? 0) + (row.quantity ?? 0);
  }
  return q;
}

/** Reprice start/accept, split out of QuoteActionsProvider (CONF-140 Phase 4
 *  task 2) to keep that provider's body under the 100-line function budget.
 *  Extracted verbatim from App.js's startReprice/acceptReprice (CONF-106
 *  part 3) — the dialog itself is Phase 6. */
export function useRepriceActions({ exec, config, priceBook, bom, user, vendor, priceEditions, showToast }: Deps) {
  const [repriceDiff, setRepriceDiff] = useState<RepriceState | null>(null);

  const qtyByPartNumber = useMemo(() => sumQtyByPartNumber(bom), [bom]);

  const startReprice = useCallback(async () => {
    try {
      // asOf: the LIVE edition's asOf, not the frozen basis's own — reprice
      // answers "what would this cost against TODAY's published price book,"
      // the same cutoff useExecPricing's own live fetch uses (App.js:595-606).
      // Passing undefined makes resolveAt skip the transaction-time filter
      // entirely (resolveAt.ts:53), so the candidate could be priced against
      // catalog rows recorded AFTER the live edition — unpublished drafts.
      const { candidate, diff } = await buildRepriceCandidate(
        fetchCatalogRows, exec.execPricing.snapshot, exec.execPricing.overrides, priceEditions.edition?.asOf);
      setRepriceDiff({ candidate, diff });
    } catch (e) {
      console.error('Reprice catalog fetch failed:', e);
      showToast({ badge: 'EXEC PRICING', title: 'Reprice unavailable', message: 'Could not fetch the current catalog — reprice unavailable.' });
    }
  }, [exec.execPricing, priceEditions.edition, showToast]);

  const acceptReprice = useCallback(async () => {
    if (!repriceDiff) return;
    const { candidate } = repriceDiff;
    const nextPrices = applyOverrides(candidate, exec.execPricing.overrides).customPriceDollars;
    const events = buildPersistEvents('reprice', {
      execPricing: { ...exec.execPricing, snapshot: candidate, customPrices: nextPrices },
      config, productDb: priceBook, bom,
      quoteSerial: exec.loadedQuoteIdRef.current ?? null,
      actor: actorFromUser(user), vendorId: vendor.id, appVersion: VERSION,
    });
    if (!(await recordPersistEvents(events))) {
      showToast({ badge: 'EXEC PRICING', title: 'Audit log failed', message: 'Could not record the reprice audit trail — reprice not applied.' });
      return;
    }
    exec.execPricing.rebase(candidate);
    setRepriceDiff(null);
  }, [repriceDiff, exec, config, priceBook, bom, user, vendor.id, showToast]);

  return { startReprice, acceptReprice, repriceDiff, setRepriceDiff, qtyByPartNumber };
}
